(function() {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 990px)';
  var SECTION_SELECTOR = '[data-c2b-sticky-horizontal-section]';
  var STAGE_SELECTOR = '[data-c2b-sticky-stage]';
  var SLIDER_SELECTOR = '[data-c2b-sticky-horizontal-slider]';
  var READY_TRUE = 'true';
  var READY_FALSE = 'false';

  var state = new Map();
  var observers = new Map();
  var mediaQuery = window.matchMedia ? window.matchMedia(DESKTOP_QUERY) : null;
  var scrollRaf = null;
  var measureRaf = null;
  var initialized = false;

  function isDesktop() {
    return mediaQuery ? mediaQuery.matches : window.innerWidth >= 990;
  }

  function getScrollY() {
    return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function getStickyTop(stage) {
    var computedTop = window.getComputedStyle(stage).top;
    var parsedTop = Number.parseFloat(computedTop);

    return Number.isFinite(parsedTop) ? parsedTop : 0;
  }

  function getTrack(section) {
    var sliderComponent = section.querySelector(SLIDER_SELECTOR);

    if (!sliderComponent) {
      return null;
    }

    return sliderComponent.querySelector('ul.slider, [id^="Slider-"]');
  }

  function setStyleValue(element, property, value) {
    if (element.style.getPropertyValue(property) !== value) {
      element.style.setProperty(property, value);
    }
  }

  function removeStyleValue(element, property) {
    if (element.style.getPropertyValue(property)) {
      element.style.removeProperty(property);
    }
  }

  function setReadyState(section, value) {
    if (section.dataset.c2bStickyReady !== value) {
      section.dataset.c2bStickyReady = value;
    }
  }

  function disconnectObserver(section) {
    var entry = observers.get(section);

    if (entry && entry.observer) {
      entry.observer.disconnect();
    }

    observers.delete(section);
  }

  function observeSection(section, stage, track) {
    var current;
    var observer;

    if (!window.ResizeObserver || !section || !stage || !track) {
      return;
    }

    current = observers.get(section);

    if (current && current.stage === stage && current.track === track) {
      return;
    }

    disconnectObserver(section);

    observer = new ResizeObserver(function() {
      requestMeasure();
    });

    observer.observe(stage);
    observer.observe(track);

    observers.set(section, {
      observer: observer,
      stage: stage,
      track: track
    });
  }

  function clearSection(section, resetTrack) {
    var track = resetTrack ? getTrack(section) : null;

    removeStyleValue(section, '--c2b-sticky-slider-stage-height');
    removeStyleValue(section, '--c2b-sticky-slider-scroll-distance');
    setReadyState(section, READY_FALSE);
    state.delete(section);
    disconnectObserver(section);

    if (track) {
      track.scrollLeft = 0;
    }
  }

  function measureSection(section) {
    var stage = section.querySelector(STAGE_SELECTOR);
    var track = getTrack(section);
    var maxScroll;
    var stageHeight;
    var stickyTop;
    var sectionTop;
    var scrollDistance;

    if (!isDesktop() || !stage || !track) {
      clearSection(section, true);
      return;
    }

    maxScroll = Math.max(0, Math.ceil(track.scrollWidth - track.clientWidth));
    stageHeight = Math.max(0, Math.ceil(stage.getBoundingClientRect().height));

    if (maxScroll <= 2 || stageHeight <= 0) {
      clearSection(section, true);
      return;
    }

    stickyTop = getStickyTop(stage);
    sectionTop = section.getBoundingClientRect().top + getScrollY();
    scrollDistance = maxScroll;

    setStyleValue(section, '--c2b-sticky-slider-stage-height', stageHeight + 'px');
    setStyleValue(section, '--c2b-sticky-slider-scroll-distance', scrollDistance + 'px');
    setReadyState(section, READY_TRUE);

    state.set(section, {
      track: track,
      maxScroll: maxScroll,
      scrollDistance: scrollDistance,
      start: sectionTop - stickyTop,
      end: sectionTop - stickyTop + scrollDistance
    });

    observeSection(section, stage, track);
  }

  function cleanupMissingSections() {
    state.forEach(function(_item, section) {
      if (!document.documentElement.contains(section)) {
        state.delete(section);
        disconnectObserver(section);
      }
    });

    observers.forEach(function(_entry, section) {
      if (!document.documentElement.contains(section)) {
        disconnectObserver(section);
      }
    });
  }

  function measureAll() {
    var sections = document.querySelectorAll(SECTION_SELECTOR);

    cleanupMissingSections();

    sections.forEach(function(section) {
      measureSection(section);
    });

    updateAll();
  }

  function updateAll() {
    var currentY = getScrollY();

    if (!isDesktop()) {
      return;
    }

    state.forEach(function(item, section) {
      var progress;
      var targetScrollLeft;

      if (!document.documentElement.contains(section)) {
        state.delete(section);
        disconnectObserver(section);
        return;
      }

      if (!item || !item.track || item.scrollDistance <= 0 || item.maxScroll <= 0) {
        return;
      }

      if (currentY <= item.start) {
        targetScrollLeft = 0;
      } else if (currentY >= item.end) {
        targetScrollLeft = item.maxScroll;
      } else {
        progress = (currentY - item.start) / item.scrollDistance;
        targetScrollLeft = progress * item.maxScroll;
      }

      targetScrollLeft = clamp(targetScrollLeft, 0, item.maxScroll);

      if (Math.abs(item.track.scrollLeft - targetScrollLeft) > 1) {
        item.track.scrollLeft = targetScrollLeft;
      }
    });
  }

  function requestScrollUpdate() {
    if (scrollRaf) {
      return;
    }

    scrollRaf = window.requestAnimationFrame(function() {
      scrollRaf = null;
      updateAll();
    });
  }

  function requestMeasure() {
    if (measureRaf) {
      window.cancelAnimationFrame(measureRaf);
    }

    measureRaf = window.requestAnimationFrame(function() {
      measureRaf = null;
      measureAll();
    });
  }

  function schedulePostLoadMeasures() {
    window.setTimeout(requestMeasure, 250);
    window.setTimeout(requestMeasure, 750);
    window.setTimeout(requestMeasure, 1500);
  }

  function init() {
    if (initialized) {
      requestMeasure();
      return;
    }

    initialized = true;

    measureAll();
    schedulePostLoadMeasures();

    window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    window.addEventListener('resize', requestMeasure, { passive: true });
    window.addEventListener('load', requestMeasure, { once: true });

    if (mediaQuery && typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', requestMeasure);
    } else if (mediaQuery && typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(requestMeasure);
    }

    document.addEventListener('shopify:section:load', requestMeasure);
    document.addEventListener('shopify:section:unload', requestMeasure);
    document.addEventListener('shopify:section:select', requestMeasure);
    document.addEventListener('shopify:section:deselect', requestMeasure);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
